import { cx } from "@jugl-web/utils";
import { ButtonHTMLAttributes, DetailedHTMLProps, FC } from "react";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";

type ChatButtonVariant = "primary" | "tertiary";
type ChatButtonSize = "sm" | "md";

interface ChatButtonProps
  extends DetailedHTMLProps<
    ButtonHTMLAttributes<HTMLButtonElement>,
    HTMLButtonElement
  > {
  variant?: ChatButtonVariant;
  size?: ChatButtonSize;
  isDisabled?: boolean;
  fullWidth?: boolean;
}

const variantToButtonClasses: Record<ChatButtonVariant, string> = {
  primary: "bg-dark text-white hover:bg-black disabled:hover:bg-dark",
  tertiary:
    "bg-grey-100 text-dark-800 hover:bg-grey-200 disabled:hover:bg-grey-100",
};

const sizeToButtonClasses: Record<ChatButtonSize, string> = {
  sm: "h-8",
  md: "h-10",
};

const sizeToTextClasses: Record<ChatButtonSize, string> = {
  sm: "group-hover:-translate-x-px group-disabled:group-hover:translate-x-0",
  md: "group-hover:-translate-x-0.5 group-disabled:group-hover:translate-x-0",
};

const sizeToArrowIconClasses: Record<ChatButtonSize, string> = {
  sm: "group-hover:translate-x-px group-disabled:group-hover:translate-x-0",
  md: "group-hover:translate-x-0.5 group-disabled:group-hover:translate-x-0",
};

export const ChatButton: FC<ChatButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  isDisabled,
  fullWidth,
  className,
  ...props
}) => {
  return (
    <button
      type="button"
      className={cx(
        "jugl__focusable-outline group inline-flex items-center justify-center gap-1 rounded-3xl px-3",
        "font-secondary text-sm font-normal transition",
        "cursor-pointer active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 disabled:active:scale-100",
        fullWidth && "w-full",
        variantToButtonClasses[variant],
        sizeToButtonClasses[size],
        className
      )}
      disabled={isDisabled}
      {...props}
    >
      <span className={cx("transition-transform", sizeToTextClasses[size])}>
        {children}
      </span>
      {variant === "primary" && (
        <ArrowRightIcon
          className={cx(
            "shrink-0 transition-transform",
            sizeToArrowIconClasses[size]
          )}
        />
      )}
    </button>
  );
};
